/**
 * /admin/slides/config — 슬라이드 운영 시스템 프롬프트 + 가이드 메시지 + 기본 디자인 토큰.
 *
 * wiki/Config.tsx 와 동일 패턴:
 *  - 빈 컬럼은 서버에서 default-prompts.ts fallback 으로 채워서 응답 → 사용자가 적용 중인 텍스트를 본다.
 *  - 저장 시 모든 필드를 update — 비어있는 그대로 두고 싶으면 default 가 다시 쓰인다.
 *  - "기본값으로 리셋" 버튼은 default-prompts.ts 상수를 컬럼에 직접 기록.
 */
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type { SlideDesign } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface FieldDef {
  key:
    | 'authoringSystemPrompt'
    | 'tsxAuthoringSystemPrompt'
    | 'outlineSystemPrompt'
    | 'ingestSystemPrompt'
    | 'themeChoiceSystemPrompt'
    | 'chatInjectPrompt'
    | 'guideFirstMessage';
  label: string;
  hint: string;
  rows: number;
}

const FIELDS: FieldDef[] = [
  {
    key: 'authoringSystemPrompt',
    label: 'blocks 모드 작성 가이드',
    hint: 'slide-creator 가 blocks 모드 페이지를 만들 때 시스템 메시지로 주입. layout / Block 종류 / 한 페이지 한 메시지 규율 등.',
    rows: 14,
  },
  {
    key: 'tsxAuthoringSystemPrompt',
    label: 'TSX 모드 작성 가이드',
    hint: '자유 TSX 페이지 작성 규율 — 1920×1080, 절대 픽셀, 디자인 토큰 var(--osd-*), useAsset 헬퍼 등.',
    rows: 14,
  },
  {
    key: 'outlineSystemPrompt',
    label: '아웃라인 추천 가이드',
    hint: '사용자 첫 입력이 빈약하거나 분량 큰 작업 전에 모델이 아웃라인을 system.ask_user 로 승인받게 하는 규율.',
    rows: 8,
  },
  {
    key: 'ingestSystemPrompt',
    label: '자료 → 슬라이드 변환 가이드',
    hint: '마크다운/위키페이지/URL 을 슬라이드로 변환하는 절차.',
    rows: 8,
  },
  {
    key: 'themeChoiceSystemPrompt',
    label: '테마 추천 가이드',
    hint: '디자인 톤 미정 시 모델이 테마 후보를 제안할 때 사용.',
    rows: 8,
  },
  {
    key: 'chatInjectPrompt',
    label: '매 turn 시스템 메시지 (slide-creator 활성 시)',
    hint: '슬라이드 컨텍스트 + 작성 규율 위에 매번 추가되는 short prompt — 도구 우선순위, 회신 톤 등.',
    rows: 8,
  },
  {
    key: 'guideFirstMessage',
    label: '새 슬라이드 첫 어시스턴트 메시지',
    hint: 'POST /api/slides 시 chat_session 에 자동 삽입되는 가이드. 사용자가 무엇을 적어야 좋은 결과가 나오는지 설명.',
    rows: 10,
  },
];

export function AdminSlidesConfig() {
  const qc = useQueryClient();
  const cfgQuery = useQuery({
    queryKey: ['admin', 'slides', 'config'],
    queryFn: api.adminSlidesConfig,
  });

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [designJson, setDesignJson] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    const cfg = cfgQuery.data;
    if (!cfg) return;
    setDraft({
      authoringSystemPrompt: cfg.authoringSystemPrompt,
      tsxAuthoringSystemPrompt: cfg.tsxAuthoringSystemPrompt,
      outlineSystemPrompt: cfg.outlineSystemPrompt,
      ingestSystemPrompt: cfg.ingestSystemPrompt,
      themeChoiceSystemPrompt: cfg.themeChoiceSystemPrompt,
      chatInjectPrompt: cfg.chatInjectPrompt,
      guideFirstMessage: cfg.guideFirstMessage,
    });
    setDesignJson(JSON.stringify(cfg.defaultDesign, null, 2));
  }, [cfgQuery.data]);

  const updateMut = useMutation({
    mutationFn: api.adminSlidesConfigUpdate,
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'slides', 'config'], data);
      setSavedMsg('저장됨');
      setTimeout(() => setSavedMsg(null), 3000);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const resetMut = useMutation({
    mutationFn: api.adminSlidesConfigResetPrompts,
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'slides', 'config'], data);
      setDraft({
        authoringSystemPrompt: data.authoringSystemPrompt,
        tsxAuthoringSystemPrompt: data.tsxAuthoringSystemPrompt,
        outlineSystemPrompt: data.outlineSystemPrompt,
        ingestSystemPrompt: data.ingestSystemPrompt,
        themeChoiceSystemPrompt: data.themeChoiceSystemPrompt,
        chatInjectPrompt: data.chatInjectPrompt,
        guideFirstMessage: data.guideFirstMessage,
      });
      setDesignJson(JSON.stringify(data.defaultDesign, null, 2));
      setSavedMsg('기본값으로 복원됨');
      setTimeout(() => setSavedMsg(null), 3000);
    },
  });

  const onSave = () => {
    setError(null);
    let parsedDesign: SlideDesign | undefined;
    if (designJson.trim().length > 0) {
      try {
        parsedDesign = JSON.parse(designJson) as SlideDesign;
      } catch {
        setError('기본 디자인 토큰 JSON 이 올바르지 않습니다.');
        return;
      }
    }
    updateMut.mutate({
      authoringSystemPrompt: draft.authoringSystemPrompt,
      tsxAuthoringSystemPrompt: draft.tsxAuthoringSystemPrompt,
      outlineSystemPrompt: draft.outlineSystemPrompt,
      ingestSystemPrompt: draft.ingestSystemPrompt,
      themeChoiceSystemPrompt: draft.themeChoiceSystemPrompt,
      chatInjectPrompt: draft.chatInjectPrompt,
      guideFirstMessage: draft.guideFirstMessage,
      defaultDesign: parsedDesign,
    });
  };

  return (
    <PageShell>
      <PageHeader
        title="슬라이드 시스템 프롬프트"
        description="slide-creator 스킬이 사용하는 5종 시스템 프롬프트 + 가이드 메시지 + 기본 디자인 토큰. 빈 칸으로 저장하면 기본값으로 fallback."
      />

      {cfgQuery.isLoading && <p className="text-sm text-foreground-muted">불러오는 중…</p>}

      {cfgQuery.data && (
        <div className="space-y-6">
          {FIELDS.map((f) => (
            <section key={f.key} className="space-y-2">
              <label htmlFor={`f-${f.key}`} className="block">
                <div className="text-sm font-medium">{f.label}</div>
                <div className="text-xs text-foreground-muted mt-0.5">{f.hint}</div>
              </label>
              <textarea
                id={`f-${f.key}`}
                value={draft[f.key] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                rows={f.rows}
                className="w-full rounded-md border border-border bg-surface-muted/30 p-3 text-sm font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </section>
          ))}

          <section className="space-y-2">
            <label htmlFor="default-design" className="block">
              <div className="text-sm font-medium">기본 디자인 토큰 (JSON)</div>
              <div className="text-xs text-foreground-muted mt-0.5">
                새 슬라이드 생성 시 사용. palette / fonts / typeScale / radius.
              </div>
            </label>
            <textarea
              id="default-design"
              value={designJson}
              onChange={(e) => setDesignJson(e.target.value)}
              rows={10}
              className="w-full rounded-md border border-border bg-surface-muted/30 p-3 text-xs font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </section>

          {error && (
            <div className="px-3 py-2 rounded-md bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}
          {savedMsg && (
            <div className="px-3 py-2 rounded-md bg-accent/10 text-accent text-sm">{savedMsg}</div>
          )}

          <div className="flex gap-2 pt-2">
            <Button onClick={onSave} disabled={updateMut.isPending}>
              {updateMut.isPending ? '저장 중…' : '저장'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (confirm('모든 시스템 프롬프트를 기본값으로 복원하시겠어요?')) {
                  resetMut.mutate();
                }
              }}
              disabled={resetMut.isPending}
            >
              기본값으로 리셋
            </Button>
            {cfgQuery.data.updatedAt && (
              <div className="text-xs text-foreground-muted self-center ml-2">
                마지막 수정: {new Date(cfgQuery.data.updatedAt).toLocaleString('ko-KR')}
              </div>
            )}
          </div>
        </div>
      )}
    </PageShell>
  );
}
